import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

const EXIT_MS = 600;
const HOLD_MS = 100;
const ENTER_MS = 950;
const FAILSAFE_MS = 3500;
// Page never fully disappears: dims to 25% for a softer, less flashy swap
const FADE_DEPTH = 0.75;

type Phase = 'idle' | 'exiting' | 'entering';

const basePath = `/${__BASE_PATH__.split('/').filter(Boolean).join('/')}`.replace(/\/$/, '');

function stripBase(pathname: string) {
  if (basePath && pathname.startsWith(basePath)) return pathname.slice(basePath.length) || '/';
  return pathname || '/';
}

function reducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Single owner of route scroll position + global soft fade between routes.
 * Scroll is set in the layout phase (before paint), so a route is never shown at a stale position.
 */
export default function RouteTransition() {
  const location = useLocation();
  const navType = useNavigationType();
  const navigate = useNavigate();
  const phase = useRef<Phase>('idle');
  const positions = useRef(new Map<string, number>());
  const currentKey = useRef(location.key);
  const pathRef = useRef(location.pathname);
  const prevPath = useRef(location.pathname);
  const timers = useRef<number[]>([]);

  const clearTimers = () => { timers.current.forEach((t) => window.clearTimeout(t)); timers.current = []; };

  // Page cross-fade: only opacity on the app root (no transforms, so fixed/pinned elements are unaffected)
  const cleanupTimer = useRef(0);
  const setOverlay = (coverage: number, ms: number, ease: string, block: boolean) => {
    const el = document.getElementById('root');
    if (!el) return;
    window.clearTimeout(cleanupTimer.current);
    el.style.transition = `opacity ${ms}ms ${ease}`;
    el.style.opacity = String(1 - coverage * FADE_DEPTH);
    el.style.pointerEvents = block ? 'none' : '';
    if (coverage === 0) {
      // Drop inline styles once fully visible so nothing lingers
      cleanupTimer.current = window.setTimeout(() => {
        el.style.transition = '';
        el.style.opacity = '';
      }, ms + 50);
    }
  };

  const finish = () => {
    clearTimers();
    phase.current = 'idle';
    setOverlay(0, ENTER_MS, 'cubic-bezier(0.25, 0.1, 0.25, 1)', false);
  };

  // We own history scroll positions (PUSH = top, POP = saved position)
  useEffect(() => {
    const prev = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    const onScroll = () => { positions.current.set(currentKey.current, window.scrollY); };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.history.scrollRestoration = prev;
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  // Scroll lifecycle — runs before the browser paints the new route
  useLayoutEffect(() => {
    currentKey.current = location.key;
    pathRef.current = location.pathname;
    const html = document.documentElement;
    const jump = (top: number) => window.scrollTo({ top, left: 0, behavior: 'instant' });
    const saved = navType === 'POP' ? positions.current.get(location.key) : undefined;
    let raf = 0;

    if (saved !== undefined) {
      // Reserve height so the target isn't clamped before the Hero pin spacer exists
      html.style.minHeight = `${saved + window.innerHeight}px`;
      jump(saved);
      let frames = 0;
      const settle = () => {
        frames += 1;
        if (Math.abs(window.scrollY - saved) > 1) jump(saved);
        if (frames < 6) raf = requestAnimationFrame(settle);
        else html.style.minHeight = '';
      };
      raf = requestAnimationFrame(settle);
    } else if (location.hash) {
      const id = decodeURIComponent(location.hash.slice(1));
      const toTarget = () => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'instant', block: 'start' });
        return Boolean(el);
      };
      if (!toTarget()) jump(0);
      // Re-align once after layout-affecting effects (e.g. pin spacers) have run
      let frames = 0;
      const settle = () => { frames += 1; toTarget(); if (frames < 3) raf = requestAnimationFrame(settle); };
      raf = requestAnimationFrame(settle);
    } else {
      jump(0);
    }

    // Fallback: route changed without our click handler (e.g. editor preview or programmatic navigate).
    // Cover instantly before paint, then run the same reveal.
    const routeChanged = prevPath.current !== location.pathname;
    prevPath.current = location.pathname;
    if (phase.current === 'idle' && routeChanged && navType !== 'POP' && !reducedMotion()) {
      setOverlay(1, 0, 'linear', true);
      phase.current = 'exiting';
    }

    if (phase.current === 'exiting') {
      phase.current = 'entering';
      // Two frames: let the destination's effects (incl. Hero ScrollTrigger refresh) settle under the cover
      let r2 = 0;
      let hold = 0;
      const r1 = requestAnimationFrame(() => { r2 = requestAnimationFrame(() => { hold = window.setTimeout(finish, HOLD_MS); }); });
      return () => { cancelAnimationFrame(raf); cancelAnimationFrame(r1); cancelAnimationFrame(r2); window.clearTimeout(hold); html.style.minHeight = ''; };
    }
    return () => { cancelAnimationFrame(raf); html.style.minHeight = ''; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Intercept internal cross-route link clicks to run the fade
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as HTMLElement | null)?.closest?.('a');
      if (!a || !a.href || a.hasAttribute('download')) return;
      if (a.target && a.target !== '_self') return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      const path = stripBase(url.pathname);
      // Same-route anchors stay immediate, except links explicitly opted in (e.g. Book Appointment on /visit)
      if (path === pathRef.current && !a.hasAttribute('data-route-fade')) return;
      if (reducedMotion()) return; // router navigates instantly; scroll still handled above

      e.preventDefault();
      if (phase.current !== 'idle') return; // ignore rapid repeat clicks
      positions.current.set(currentKey.current, window.scrollY);
      phase.current = 'exiting';
      setOverlay(1, EXIT_MS, 'cubic-bezier(0.4, 0, 0.2, 1)', true);
      const to = `${path}${url.search}${url.hash}`;
      timers.current.push(window.setTimeout(() => {
        try { navigate(to); } catch { finish(); }
      }, EXIT_MS));
      timers.current.push(window.setTimeout(() => { if (phase.current !== 'idle') finish(); }, FAILSAFE_MS));
    };
    document.addEventListener('click', onClick, true);
    return () => { document.removeEventListener('click', onClick, true); clearTimers(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  return null;
}